import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';




export default function AdminWorkspaces() {
  const [spaces, setSpaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: undefined,
    location_id: undefined,
    description: '',
    capacity: '',
    images: '',
    amenities: '',
    price: '',
    virtual_tour_link: '',
    show_virtual_tour: false,
    badges: '',
    is_active: true,
    google_maps_link: '',
    show_directions: true,
    map_address: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [spacesRes, locationsRes, amenitiesRes] = await Promise.all([
          apiClient.get('/spaces'),
          apiClient.get('/locations'),
          apiClient.get('/amenities')
        ]);
        setSpaces(spacesRes.data);
        setLocations(locationsRes.data);
        setAmenities(amenitiesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const fetchSpaces = async () => {
    try {
      const response = await apiClient.get('/spaces');
      setSpaces(response.data);
    } catch (error) {
      console.error('Error fetching spaces:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.location_id || formData.location_id.trim() === '') {
      toast.error('Please enter a location');
      return;
    }
    
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Please enter a workspace name');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        images: formData.images.split(',').map(img => img.trim()).filter(Boolean),
        amenities: selectedAmenities.length > 0 ? selectedAmenities : formData.amenities.split(',').map(a => a.trim()).filter(Boolean),
        badges: formData.badges.split(',').map(b => b.trim()).filter(Boolean),
        id: editingSpace?.id || undefined,
        capacity: formData.capacity || '',
        type: formData.type || '',
        description: formData.description || '',
        price: formData.price || '',
        google_maps_link: formData.google_maps_link || '',
        show_directions: formData.show_directions !== false,
        map_address: formData.map_address || ''
      };

      console.log('Submitting workspace payload:', payload);

      if (editingSpace) {
        const response = await apiClient.put(`/admin/spaces/${editingSpace.id}`, payload);
        console.log('Update response:', response);
        toast.success('Workspace updated successfully!');
      } else {
        const response = await apiClient.post('/admin/spaces', payload);
        console.log('Create response:', response);
        toast.success('Workspace created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchSpaces();
    } catch (error) {
      console.error('Error saving workspace:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save workspace';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (space) => {
    setEditingSpace(space);
    setSelectedAmenities(space.amenities || []);
    setFormData({
      ...space,
      images: space.images.join(', '),
      amenities: space.amenities.join(', '),
      badges: space.badges.join(', '),
      google_maps_link: space.google_maps_link || '',
      show_directions: space.show_directions !== false,
      map_address: space.map_address || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this workspace?')) {
      try {
        await apiClient.delete(`/admin/spaces/${id}`);
        toast.success('Workspace deleted successfully!');
        fetchSpaces();
      } catch (error) {
        toast.error('Failed to delete workspace');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: undefined,
      location_id: undefined,
      description: '',
      capacity: '',
      images: '',
      amenities: '',
      price: '',
      virtual_tour_link: '',
      show_virtual_tour: false,
      badges: '',
      is_active: true,
      google_maps_link: '',
      show_directions: true,
      map_address: ''
    });
    setSelectedAmenities([]);
    setEditingSpace(null);
  };

  const toggleAmenity = (amenityName) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityName)
        ? prev.filter(a => a !== amenityName)
        : [...prev, amenityName]
    );
  };

  const getLocationName = (locationId) => {
    // location_id is now a text field, just return it directly
    return locationId || 'Not specified';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Workspaces</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Workspace
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spaces.map((space) => (
            <div key={space.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src={space.images[0] || '/placeholder.jpg'} alt={space.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{space.name}</h3>
                <p className="text-gray-600 text-sm mb-1">{space.type}</p>
                <p className="text-gray-500 text-sm mb-2">{getLocationName(space.location_id)}</p>
                <p className="text-gray-600 text-sm mb-4">Capacity: {space.capacity}</p>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    onClick={() => window.open(`/space/${space.id}`, '_blank')} 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Eye size={16} className="mr-1" />
                    View
                  </Button>
                  <Button onClick={() => handleEdit(space)} size="sm" variant="outline">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(space.id)} size="sm" variant="destructive">
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSpace ? 'Edit Workspace' : 'Add New Workspace'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Workspace Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Premium Private Office"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Input
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  placeholder="e.g., Coworking, Private Office, Meeting Room (optional)"
                />
              </div>
              <div>
                <Label>Location *</Label>
                <Input
                  value={formData.location_id || ''}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  placeholder="e.g., Sarjapur Road, Hebbal"
                  required
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Capacity</Label>
                <Input
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="e.g., 1-10 people (optional)"
                />
              </div>
              <div>
                <Label>Price</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., ₹8,000/month (optional)"
                />
              </div>
            </div>
            <ImageUpload
              label="Workspace Images (Multiple)"
              value={formData.images}
              onChange={(urls) => setFormData({ ...formData, images: urls })}
              required={true}
              multiple={true}
              preview={true}
            />
            <div>
              <Label>Amenities</Label>
              <p className="text-sm text-gray-500 mb-2">Select from available amenities</p>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50">
                {amenities.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {amenities.map((amenity) => (
                      <label
                        key={amenity.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAmenities.includes(amenity.name)}
                          onChange={() => toggleAmenity(amenity.name)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{amenity.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No amenities available. Add amenities from the Amenities section first.</p>
                )}
              </div>
              {selectedAmenities.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600">Selected: {selectedAmenities.join(', ')}</p>
                </div>
              )}
            </div>
            <div>
              <Label>Badges (comma separated)</Label>
              <Input
                value={formData.badges}
                onChange={(e) => setFormData({ ...formData, badges: e.target.value })}
                placeholder="Newly Open, Limited Offer"
              />
            </div>
            <div className="border rounded-lg p-4 bg-blue-50 space-y-4">
              <h3 className="font-semibold text-gray-800">Location & Map Settings</h3>
              <div>
                <Label>Google Maps Link</Label>
                <Input
                  value={formData.google_maps_link}
                  onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
                  placeholder="https://maps.app.goo.gl/... or full Google Maps URL"
                />
                <p className="text-xs text-gray-500 mt-1">Paste the Google Maps share link for directions</p>
              </div>
              <div>
                <Label>Map Embed Address</Label>
                <Input
                  value={formData.map_address}
                  onChange={(e) => setFormData({ ...formData, map_address: e.target.value })}
                  placeholder="e.g., The Circuit, Sarjapur Road, Bangalore"
                />
                <p className="text-xs text-gray-500 mt-1">Address shown on the embedded map (use a specific address for accurate pin)</p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="show_directions"
                  checked={formData.show_directions}
                  onChange={(e) => setFormData({ ...formData, show_directions: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="show_directions" className="cursor-pointer">Show "Get Directions" Button</Label>
              </div>
            </div>
            <div>
              <Label>Virtual Tour Link (Optional)</Label>
              <Input
                value={formData.virtual_tour_link}
                onChange={(e) => setFormData({ ...formData, virtual_tour_link: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show_virtual_tour"
                checked={formData.show_virtual_tour}
                onChange={(e) => setFormData({ ...formData, show_virtual_tour: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="show_virtual_tour" className="cursor-pointer">Show Virtual Tour</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Workspace
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}